/**
 * Public → private route boundary guard.
 *
 * Once a third-party vendor script (GA4 / Ads / HubSpot / the Meta container)
 * is in the page it installs its own `history` and `popstate` listeners and
 * reads the RAW address bar. A passive React effect that "just doesn't send a
 * page_view" is therefore NOT enough: the SPA transition itself hands a private
 * URL — a customer's quote token, a booking confirmation, an admin screen — to
 * every vendor already loaded.
 *
 * So at the boundary we do a FULL DOCUMENT NAVIGATION instead of a soft SPA
 * transition. The current document (with its vendor listeners) is torn down,
 * and the private route loads in a fresh document where no vendor is ever
 * initialized, because `applyLoaders()` refuses to load a vendor on a private
 * route.
 *
 * Coverage:
 *   - clicks: React Router's Link and every programmatic `navigate()` go
 *     through `history.pushState` / `replaceState`, which are patched here;
 *   - programmatic: same path — patched.
 *   - back / forward: `popstate` cannot be cancelled. We register our handler
 *     at app start, BEFORE any vendor script is fetched, so ours runs first and
 *     immediately triggers a document reload. This is a best-effort mitigation,
 *     not a guarantee: a vendor listener registered on the same event may still
 *     observe the URL in the moments before the reload commits. Documented
 *     honestly rather than claimed as airtight.
 *
 * The guard only engages once a vendor has actually loaded — with no vendor in
 * the page there is nothing to hide the URL from, and a needless full page load
 * would just be slower.
 */

import { isPublicTrackableRoute } from "./public-routes";

let installed = false;
let vendorLoaded = false;

/** Called by the consent layer the first time any vendor script is inserted. */
export function markVendorLoaded(): void {
  vendorLoaded = true;
}

export function __resetBoundaryForTests(): void {
  installed = false;
  vendorLoaded = false;
}

function pathOf(url: string | URL | null | undefined): string | null {
  if (url === null || url === undefined) return null;
  try {
    return new URL(String(url), window.location.href).pathname;
  } catch {
    return null;
  }
}

/** True when leaving the current (public) route for a private one. */
function crossesBoundary(targetPath: string | null): boolean {
  if (!vendorLoaded) return false;
  if (!targetPath) return false;
  if (isPublicTrackableRoute(targetPath)) return false;
  return isPublicTrackableRoute(window.location.pathname);
}

export function installVendorRouteBoundary(): void {
  if (installed) return;
  if (typeof window === "undefined" || typeof history === "undefined") return;
  installed = true;

  const originalPush = history.pushState.bind(history);
  const originalReplace = history.replaceState.bind(history);

  history.pushState = function patchedPushState(data, unused, url) {
    const target = pathOf(url as string | URL | null);
    if (crossesBoundary(target)) {
      // Full document navigation: the current document — and every vendor
      // listener in it — is discarded before the private URL exists.
      window.location.assign(String(url));
      return;
    }
    return originalPush(data, unused, url as string | URL | null | undefined);
  };

  history.replaceState = function patchedReplaceState(data, unused, url) {
    const target = pathOf(url as string | URL | null);
    if (crossesBoundary(target)) {
      window.location.replace(String(url));
      return;
    }
    return originalReplace(data, unused, url as string | URL | null | undefined);
  };

  // Back / forward. Not cancellable — see the module note.
  window.addEventListener("popstate", () => {
    if (!vendorLoaded) return;
    if (isPublicTrackableRoute(window.location.pathname)) return;
    window.location.reload();
  });
}
